/** One currency's income statement: Sales through Net income, one column per
 * month in range plus a period total when the range spans more than one
 * month. Pure/presentational — the caller (FinancePage) loops per currency
 * and supplies the month set and per-cell markers. */
import { Fragment } from 'react';
import type { FinanceSummarySettlementActivity } from '@sourcito/schemas';
import { cn } from '../../../design';
import { formatFinanceDelta } from '../../../lib/finance-summary';

/** Render a footnote marker ("1", or "1,2" when a cell is explained by more
 * than one Needs-you item) as separate <sup> glyphs joined by a thin space
 * (U+2009) instead of one sup with a literal comma — and with enough
 * contrast (text-fg-secondary, font-medium) to actually read as a footnote
 * (R2 item 2, 2026-09-02 cold-viewer review). Shared with NeedsYou. */
export function FinanceMarker({ marker }: { marker: string }) {
  const parts = marker.split(',');
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={`${part}-${index}`}>
          {index > 0 && ' '}
          <sup className="text-fg-secondary font-medium">{part}</sup>
        </Fragment>
      ))}
    </>
  );
}

/** One row per statement line, keyed for FinancePage's per-cell marker map. */
export type StatementRowKey =
  | 'sales'
  | 'amazonFees'
  | 'proceeds'
  | 'cogs'
  | 'warehouse'
  | 'overhead'
  | 'netIncome';

/** column id ('YYYY-MM', or the literal 'period') -> row key -> marker text
 * (e.g. "1" or, when more than one Needs-you item explains the same cell,
 * "1,2"). A marker renders on a cell whether it holds a value or a dash — a
 * value with a marker means "provisional". */
export type StatementMarkers = Readonly<
  Record<string, Readonly<Partial<Record<StatementRowKey, string>>>>
>;

export interface IncomeStatementProps {
  /** One currency's settlement activity. */
  activity: FinanceSummarySettlementActivity;
  /** 'YYYY-MM' keys for every column, in chronological order. */
  monthKeys: readonly string[];
  /** Per-cell footnote markers — see StatementMarkers. */
  markers: StatementMarkers;
  /** Visible caption (multi-currency pages); sr-only otherwise — the
   * pickers and status sentence already say which currency/period when
   * there's only one table on the page. */
  showCaption?: boolean;
}

type MonthlyRow = FinanceSummarySettlementActivity['monthly'][number];
type StatementSource = MonthlyRow | FinanceSummarySettlementActivity;

interface StatementRow {
  key: StatementRowKey;
  label: string;
  kind: 'line' | 'subtotal' | 'total';
  /** Costs render negated (a true minus sign) — Sales/Proceeds/Net income
   * render the server's own sign as-is. */
  cost: boolean;
  cell: (source: StatementSource) => number | null | undefined;
}

const ROWS: readonly StatementRow[] = [
  {
    key: 'sales',
    label: 'Sales',
    kind: 'line',
    cost: false,
    cell: (s) => s.settlementRevenueCents,
  },
  {
    key: 'amazonFees',
    label: 'Amazon fees',
    kind: 'line',
    cost: true,
    cell: (s) => s.amazonNetChargeCents,
  },
  {
    key: 'proceeds',
    label: 'Proceeds',
    kind: 'subtotal',
    cost: false,
    cell: (s) => s.netProceedsBeforeCogsCents,
  },
  {
    key: 'cogs',
    label: 'Cost of goods sold',
    kind: 'line',
    cost: true,
    cell: (s) => s.landedCogsCents,
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    kind: 'line',
    cost: true,
    cell: (s) => s.warehousePeriodExpenseCents,
  },
  {
    key: 'overhead',
    label: 'Overhead',
    kind: 'line',
    cost: true,
    cell: (s) => s.operatingExpenseCents,
  },
  {
    key: 'netIncome',
    label: 'Net income',
    kind: 'total',
    cost: false,
    cell: (s) => s.netIncomeCents,
  },
];

function monthColumnLabel(month: string, showYear: boolean): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: showYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  });
}

function cellText(cents: number | null, cost: boolean, currency: string): string {
  if (cents == null) return '—';
  return formatFinanceDelta(cost ? -cents : cents, currency);
}

function netIncomeColorClass(cents: number | null): string {
  if (cents == null) return '';
  return cents >= 0 ? 'text-positive' : 'text-negative';
}

export function IncomeStatement({
  activity,
  monthKeys,
  markers,
  showCaption = false,
}: IncomeStatementProps) {
  const monthly = new Map(activity.monthly.map((row) => [row.month, row]));
  const years = new Set(monthKeys.map((month) => month.slice(0, 4)));
  const showYear = years.size > 1;
  const showPeriodColumn = monthKeys.length > 1;
  const periodColumnLabel = showPeriodColumn
    ? `${monthColumnLabel(monthKeys[0]!, false)}–${monthColumnLabel(monthKeys[monthKeys.length - 1]!, false)}`
    : null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-sm border-collapse">
        <caption
          className={cn(
            'caption-top text-left text-sm font-medium text-fg-primary mb-2',
            !showCaption && 'sr-only'
          )}
        >
          Income statement · {activity.currency}
        </caption>
        <thead>
          <tr className="text-xs text-fg-tertiary">
            <th scope="col" className="text-left font-normal py-2 pr-3" />
            {monthKeys.map((month) => (
              <th
                key={month}
                scope="col"
                className="text-right font-normal py-2 px-3 tabular-nums whitespace-nowrap"
              >
                {monthColumnLabel(month, showYear)}
              </th>
            ))}
            {showPeriodColumn && (
              <th
                scope="col"
                className="text-right font-normal py-2 pl-3 tabular-nums whitespace-nowrap"
              >
                {periodColumnLabel}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr
              key={row.label}
              className={cn(
                row.kind === 'subtotal' && 'border-t border-border-subtle font-semibold',
                row.kind === 'total' && 'border-t-2 border-border-default font-bold'
              )}
            >
              <th scope="row" className="text-left font-inherit py-2 pr-3 text-fg-secondary">
                {row.label}
              </th>
              {monthKeys.map((month) => {
                const source = monthly.get(month);
                const cents = source ? (row.cell(source) ?? null) : null;
                const marker = markers[month]?.[row.key];
                return (
                  <td
                    key={month}
                    className={cn(
                      'text-right py-2 px-3 tabular-nums',
                      row.kind === 'total' && netIncomeColorClass(cents)
                    )}
                  >
                    {cellText(cents, row.cost, activity.currency)}
                    {marker && <FinanceMarker marker={marker} />}
                  </td>
                );
              })}
              {showPeriodColumn &&
                (() => {
                  const cents = row.cell(activity) ?? null;
                  const marker = markers['period']?.[row.key];
                  return (
                    <td
                      className={cn(
                        'text-right py-2 pl-3 tabular-nums',
                        row.kind === 'total' && netIncomeColorClass(cents)
                      )}
                    >
                      {cellText(cents, row.cost, activity.currency)}
                      {marker && <FinanceMarker marker={marker} />}
                    </td>
                  );
                })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
